<template>
	<div class="scheduling-container">
		<div no-gutters class="editor_top_bar header">
			<BlockEditorTopHeader :showParams="true" :integrationSelector="false" headerIcons="svg-ply-scheduled-webhook" />
		</div>
		<div class="scheduling pb-6">
			<div class="px-4">
				<div class="my-3">When will this feature run?</div>
				<AppSelect v-model="config.intervalType" :items="intervalOptions" outlined dense />
				<div v-if="showIntervalDays" class="ml-8 mt-4 sub-text">Will run every week on</div>
				<AppSelect
					v-if="showIntervalDays"
					v-model="config.intervalDays"
					:multiple="true"
					:items="daysOptions"
					dense
					outlined
					placeholder="Choose days"
					class="days"
				/>
				<div class="date-time">
					<div v-if="showMinuteSlider" class="display-flex flex-justify-space-between mt-4 ml-1">
						<div class="mt-1 mr-1">Will run every hour at</div>
						<div class="display-flex flex-row">
							<AppTextField class="minutes-const mr-1" v-model="minuteConst" disabled outlined></AppTextField>
							<div class="mt-1 mr-1">:</div>
							<AppTextField class="minutes" type="number" v-model="minutes" outlined :max="59" :min="0" :rules="minRules"> </AppTextField>
						</div>
					</div>
					<AppTimePickerMenu
						v-if="showIntervalTime"
						v-model="config.intervalTime"
						format="24hr"
						:label="config.intervalType === INTERVAL_OPTIONS.one_week ? 'At' : 'Will run every day at'"
						:allowed-hours="config.intervalType === INTERVAL_OPTIONS.one_hour ? [0] : undefined"
					/>
				</div>
			</div>
			<v-divider class="my-4"></v-divider>
			<div class="px-4">
				<div class="my-3">When will it start?</div>
				<AppSelect v-model="scheduleTimeStart" :items="scheduleTimeStartOptions" outlined dense />
				<div v-if="scheduleTimeStart" class="date-time display-flex flex-column">
					<AppDatePickerMenu v-model="config.startDate" :min="minDate" label="Starting day" />
					<AppTimePickerMenu v-model="config.startTime" format="24hr" label="Starting time" />
				</div>
			</div>
			<v-divider class="my-4"></v-divider>
			<div class="px-4">
				<div class="my-3">When will it end?</div>
				<AppSelect v-model="config.untilType" :items="untilWhenOptions" outlined dense />

				<div v-if="config.untilType === UNTIL_OPTIONS.date_time" class="date-time display-flex flex-column mt-4">
					<AppDatePickerMenu v-model="config.untilDate" :min="minDate" label="Ending day" />
					<AppTimePickerMenu v-model="config.untilTime" format="24hr" label="Ending time" />
				</div>

				<div v-if="config.untilType === UNTIL_OPTIONS.x_times" class="mt-4">
					<div class="mt-4 display-flex flex-row">
						<div class="ml-2 mr-1 mt-1">Will stop after</div>
						<AppTextField class="x-times" type="number" v-model="config.untilX" outlined :min="1" :rules="xTimesRules"> </AppTextField>
						<div class="ml-1 mt-1">times</div>
					</div>
					<div class="description">
						<div class="description-sub">Please notice that on publish this task will restart. Number of runs will initialize to 0</div>
					</div>
				</div>
			</div>
			<v-divider class="my-4"></v-divider>
			<div class="px-4">
				<div class="mt-4 display-flex flex-row">
					<div class="my-3 pr-1">Timezone</div>
					<AppTooltip
						top
						:text="`<div> If you and your team are not in the same timezones, we recommend you stay on UTC. </div>
						<div> If not, suggested timezone is ${Intl.DateTimeFormat().resolvedOptions().timeZone}. </div>`"
					>
						<SIcon name="installation-tab-info" width="12" height="18" />
					</AppTooltip>
				</div>
				<AppSelect
					class="mt-2"
					:items="timezonesOptions"
					outlined
					dense
					placeholder="Select timezone"
					v-model="config.timezone"
				></AppSelect>
			</div>
		</div>
	</div>
</template>
<script>
import moment from 'moment-timezone';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import AppTooltip from '@/components/common/AppTooltip';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import BlockEditorTopHeader from '@/components/BlockEditor/BlockEditorTopHeader';
import { editedWorkflowVersion, selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import { updateWorkflowVersionTrigger } from '@/store/modules/selectedWorkflow/actions';
const OPEN_HOVER_DELAY_TIME = 300; // daley until open of tooltip on hover
const CLOSE_HOVER_DELAY_TIME = 0; // daley until close of tooltip on hover
const EMPTY_TIME = '00:00';

const INTERVAL_OPTIONS = {
	ten_min: '10 min',
	thirty_min: '30 min',
	one_hour: '1 hour',
	one_day: '1 day',
	one_week: '1 week',
};

const UNTIL_OPTIONS = {
	date_time: 'date_time',
	x_times: 'x_times',
	forever: 'forever',
};

const INITIAL_CONFIG = {
	intervalType: undefined,
	intervalTime: undefined,
	intervalDays: [],
	startTime: undefined,
	startDate: undefined,
	untilType: undefined,
	untilX: undefined,
	untilDate: undefined,
	untilTime: undefined,
	timezone: undefined,
};

export default {
	name: 'ScheduledCloudWorkflowBlock',
	components: {
		BlockEditorTopHeader,
		AppTooltip,
	},
	computed: {
		...mapGettersShorthand({ editedWorkflowVersion, selectedWorkflowId }),
		timezonesOptions() {
			return moment.tz.names();
		},
		workflowVersion() {
			return this.editedWorkflowVersion;
		},
		intervalOptions() {
			return [
				{ text: 'Every 10 minutes', value: INTERVAL_OPTIONS.ten_min },
				{ text: 'Every half hour', value: INTERVAL_OPTIONS.thirty_min },
				{ text: 'Every hour', value: INTERVAL_OPTIONS.one_hour },
				{ text: 'Every day', value: INTERVAL_OPTIONS.one_day },
				{ text: 'Weekly schedule', value: INTERVAL_OPTIONS.one_week },
			];
		},
		untilWhenOptions() {
			return [
				{ text: 'At a specific day and time', value: UNTIL_OPTIONS.date_time },
				{ text: 'After X times', value: UNTIL_OPTIONS.x_times },
				{ text: 'Never', value: UNTIL_OPTIONS.forever },
			];
		},
		daysOptions() {
			return [
				{ text: 'Monday', value: '1' },
				{ text: 'Tuesday', value: '2' },
				{ text: 'Wednesday', value: '3' },
				{ text: 'Thursday', value: '4' },
				{ text: 'Friday', value: '5' },
				{ text: 'Saturday', value: '6' },
				{ text: 'Sunday', value: '0' },
			];
		},
		scheduleTimeStartOptions() {
			return [
				{ text: 'Now', value: false },
				{ text: 'At a specific day and time', value: true },
			];
		},
		minDate() {
			const date = new Date();
			return date.toISOString();
		},
		showMinuteSlider() {
			return INTERVAL_OPTIONS.one_hour === this.config.intervalType;
		},
		showIntervalTime() {
			return [INTERVAL_OPTIONS.one_day, INTERVAL_OPTIONS.one_week].includes(this.config.intervalType);
		},
		showIntervalDays() {
			return INTERVAL_OPTIONS.one_week === this.config.intervalType;
		},
		minRules() {
			return [(v) => !!v || 'required', (v) => (v && v >= 0) || 'min val', (v) => (v && v <= 59) || 'max val'];
		},
		xTimesRules() {
			return [(v) => !!v || 'required', (v) => (v && v >= 1) || 'min val'];
		},
		minutes: {
			get() {
				const val = this.config.intervalTime?.split(':')?.[1] || '0';
				if (val && val.length == 2 && val[0] === '0') {
					return val[1];
				}
				return val;
			},
			set(value) {
				if (value && !isNaN(value)) {
					if (value < 10) {
						this.config.intervalTime = `00:0${value}`;
					} else {
						this.config.intervalTime = `00:${value}`;
					}
				}
			},
		},
		computedConfig() {
			// watch the entire as a new object
			return Object.assign({}, this.config);
		},
	},
	methods: {
		...mapActionsShorthand({ updateWorkflowVersionTrigger }),
		saveChanges: debounce(async function () {
			const scheduledTriggerConfig = {
				interval_type: this.config.intervalType,
				until_type: this.config.untilType,
				interval_time: this.config.intervalTime,
				timezone: this.config.timezone,
			};

			if (this.config.intervalType === INTERVAL_OPTIONS.one_week) {
				scheduledTriggerConfig.interval_days = this.config.intervalDays?.join(',');
			} else {
				this.config.intervalDays = [];
			}

			if (this.scheduleTimeStart) {
				scheduledTriggerConfig.start_date = this.config.startDate;
				scheduledTriggerConfig.start_time = this.config.startTime;
			} else {
				scheduledTriggerConfig.start_date = null;
				scheduledTriggerConfig.start_time = null;
			}

			if (this.config.untilType === UNTIL_OPTIONS.date_time) {
				scheduledTriggerConfig.until_date = this.config.untilDate;
				scheduledTriggerConfig.until_time = this.config.untilTime;

				scheduledTriggerConfig.until_x_times = null;
			} else if (this.config.untilType === UNTIL_OPTIONS.x_times) {
				scheduledTriggerConfig.until_x_times = this.config.untilX;

				scheduledTriggerConfig.until_date = null;
				scheduledTriggerConfig.until_time = null;
			} else if (this.config.untilType === UNTIL_OPTIONS.forever) {
				scheduledTriggerConfig.until_x_times = null;
				scheduledTriggerConfig.until_date = null;
				scheduledTriggerConfig.until_time = null;
			}
			await this.updateWorkflowVersionTrigger({
				workflowId: this.selectedWorkflowId,
				workflowVersionId: this.workflowVersion.id,
				config: scheduledTriggerConfig,
			});
			this.syncData();
		}, 200),
		syncData() {
			if (this.workflowVersion?.scheduled_trigger_config_version) {
				const config = this.workflowVersion.scheduled_trigger_config_version;
				this.config.intervalType = config.interval_type;

				if ([INTERVAL_OPTIONS.ten_min, INTERVAL_OPTIONS.thirty_min].includes(config.interval_type)) {
					// 10/30 min does not use the intervalTime parameter so if changed between different options we should clean this option, so we don't see weird issues with the time.
					this.config.intervalTime = EMPTY_TIME;
				} else {
					this.config.intervalTime = config.interval_time;
				}

				this.config.intervalDays = config.interval_days === '' ? [] : [...new Set(config.interval_days?.split(',') || [])];

				this.config.untilType = config.until_type;
				this.config.untilX = config.until_x_times;
				this.config.untilDate = config.until_date;
				this.config.untilTime = config.until_time;

				this.config.timezone = config.timezone;
				this.config.startTime = config.start_time;
				this.config.startDate = config.start_date;
				if (config.start_time || config.start_date) {
					this.scheduleTimeStart = true;
				} else {
					this.scheduleTimeStart = false;
				}
			}
		},
	},
	mounted() {
		this.syncData();
	},
	data() {
		return {
			config: { ...INITIAL_CONFIG },
			scheduleTimeStart: false,
			UNTIL_OPTIONS,
			INTERVAL_OPTIONS,
			OPEN_HOVER_DELAY_TIME,
			CLOSE_HOVER_DELAY_TIME,
			minuteConst: 'XX',
		};
	},
	watch: {
		computedConfig: {
			handler(newV, oldV) {
				const isInitialValue = isEqual(JSON.stringify(oldV), JSON.stringify(INITIAL_CONFIG));
				if (newV && oldV && !isInitialValue && !isEqual(newV, oldV)) {
					this.saveChanges();
				}
			},
			deep: true,
		},
	},
};
</script>
<style scoped lang="scss">
.scheduling-container {
	font-size: 14px;

	.header {
		height: 70px;
		background: #fcfcfd;
		z-index: 3;
	}
	.scheduling {
		.description {
			opacity: 0.5;
			.description-sub {
				font-size: 11px;
			}
		}
		.sub-text {
			font-size: 11px;
			opacity: 0.5;
		}
		.x-times {
			width: 280px;
		}
		.minutes-const {
			width: 45px;
		}
		.minutes {
			width: 60px;
		}
	}
}
</style>
